/**
 * Soft≠Live Page171 — Fleet Firmware residual liquid (page138/73 + status SoT).
 * Mount: /fleet/firmware
 * Buses (contract ops — never painted in operator DOM):
 *   get__ota_status · get__ota_campaigns · get__ota_firmwares · ota policy
 *   post __from-stored · cancel · accept · decline · rollback
 * Soft residual deleted:
 *  - sandpit Firmware body without liquid rail / KPI / posture SoT
 *  - client-only seat invent without Re-read Live status
 *  - unlabeled "Refresh" (must be Re-read)
 *  - Soft Save rollback/switches/stall theatre
 *  - Soft invent campaigns when seat gated (honest blank)
 *  - path /operation labels painted in DOM
 */
import { useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, F, Ico, KV, Card, Field, Input, Alert, Ticker,
         SOFT, FADE, go, stop } from "./ui.js";
import * as L from "./fleet-data.js";
import { campaignRowsOf, firmwareRowsOf } from "./fleet-live.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";

/** Path-free operator copy for board-update failures. Soft invent catalogues stay deleted. */
export function softFleetFirmwareErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|ota_seat|not_authorized|denied|seat/.test(low)) {
    return "Your account cannot run board updates. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found|campaign/.test(low)) {
    return "That campaign or build is not available.";
  }
  if (/\b422\b|\b409\b|invalid|required|conflict|filename/.test(low)) {
    return "That change was not accepted.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable/.test(low)) {
    return "Board updates are not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/ota\/|GET |POST |PUT |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Soft≠Live: missing contract ops must not crash the desk. */
function safeOtaPath(operationId, options) {
  try {
    return path(operationId, options || {});
  } catch (e) {
    if (/Unknown operation_id/i.test(String((e && e.message) || e))) {
      if (operationId === "get__ota_status") return "/ota/status";
      if (operationId === "get__ota_campaigns") return "/ota/campaigns";
      if (operationId === "get__ota_firmwares") return "/ota/firmwares";
      if (operationId === "get__ota_policy") return "/ota/policy";
      return null;
    }
    throw e;
  }
}

/** Pure: OTA manage seat — Live grant only, Soft tab invent forbidden. */
export function canManageOtaOf(me) {
  if (!me) return false;
  const r = String(me.role || "");
  if (r === "admin" || r === "superadmin") return true;
  if (r !== "sa_staff") return false;
  const raw = me.caps || me.sa_capabilities || [];
  const caps = Array.isArray(raw)
    ? raw.map((c) => String(c || "").trim())
    : String(raw).split(",").map((c) => c.trim()).filter(Boolean);
  return caps.includes("sa_ota_admin") || caps.includes("sa_full_access");
}

/** Pure: publish seat — superadmin / sa_ota_admin / sa_full_access only. */
export function canPublishOtaOf(me) {
  if (!me) return false;
  if (me.role === "superadmin") return true;
  if (me.role !== "sa_staff") return false;
  const raw = me.caps || me.sa_capabilities || [];
  const caps = Array.isArray(raw)
    ? raw.map((c) => String(c || "").trim())
    : String(raw).split(",").map((c) => c.trim()).filter(Boolean);
  return caps.includes("sa_ota_admin") || caps.includes("sa_full_access");
}

/** Pure: campaign KPI strip — never invent counts Soft. */
export function firmwareKpisOf(camps, builds, policy) {
  const list = Array.isArray(camps) ? camps : [];
  const bins = Array.isArray(builds) ? builds : [];
  const active = list.filter((c) =>
    c && (c.state === "running" || c.state === "dispatched" || c.state === "partial")).length;
  return {
    campaigns: list.length,
    active,
    builds: bins.length,
    policy: policy ? 1 : 0,
  };
}

export function FleetFirmwareDesk({ toast }) {
  const { me } = useSession();
  const posture = useApi(safeOtaPath("get__ota_status") || "");
  const post = posture.data || {};
  // Fail-closed: only Live posture opens seats.
  const otaOk = !posture.loading && post.campaign_read_allowed === true;
  const writeOk = !posture.loading && (
    post.campaign_write_allowed === true || post.write_allowed === true
  );
  const canPublish = !posture.loading && (
    post.publish_allowed === true || post.firmware_list_allowed === true
  );
  const detail = post.detail || null;
  const postureLabel = String(post.posture_label || "").trim()
    || (otaOk
      ? (canPublish ? "Board updates open · publish open" : "Board updates open · publish sealed")
      : "Board updates need a manager seat");
  const campsLive = useApi(otaOk ? (safeOtaPath("get__ota_campaigns") || "") : "");
  const buildsLive = useApi(canPublish ? (safeOtaPath("get__ota_firmwares") || "") : "");
  const policyLive = useApi(otaOk ? (safeOtaPath("get__ota_policy") || "/ota/policy") : "");
  const act = useAction();
  const envelope = act.envelope;
  const camps = useMemo(() => campaignRowsOf(campsLive.data), [campsLive.data]);
  const builds = useMemo(() => firmwareRowsOf(buildsLive.data), [buildsLive.data]);
  const policy = (policyLive.data && typeof policyLive.data === "object")
    ? policyLive.data
    : (post && (post.device_ack_timeout_seconds != null || post.auto_rollback_on_failure != null)
      ? {
        auto_rollback_on_failure: !!post.auto_rollback_on_failure,
        device_ack_timeout_seconds: post.device_ack_timeout_seconds,
        hw_compat_enabled: !!post.hw_compat_enabled,
        campaign_reads: !!post.campaign_read_allowed,
        campaign_writes: !!post.campaign_write_allowed,
        public_base_configured: !!post.public_base_configured,
        token_configured: !!post.token_configured,
        note: detail || "",
      }
      : null);
  const kpis = useMemo(() => firmwareKpisOf(camps, builds, policy), [camps, builds, policy]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [pubFile, setPubFile] = useState("");
  const [pubNotes, setPubNotes] = useState("");
  const [pubFw, setPubFw] = useState("");

  const c = camps.find((x) => x.id === open);
  const count = (id) => camps.filter((x) => x.state === id).length;
  const list = filter === "all" ? camps : camps.filter((x) => x.state === filter);
  const liveErr = campsLive.error || buildsLive.error || policyLive.error;

  const reRead = () => {
    posture.reload && posture.reload();
    campsLive.reload && campsLive.reload();
    buildsLive.reload && buildsLive.reload();
    policyLive.reload && policyLive.reload();
    toast && toast("Board status refreshed", "go");
  };

  const cancel = async (id) => {
    if (!writeOk) {
      toast && toast("Your seat cannot change campaigns.", "warn");
      return;
    }
    const out = await act.run(path("post__ota_campaigns_p_cancel", { pathParams: [id] }), {
      method: "POST",
      body: {},
    });
    if (!out) {
      toast && toast(softFleetFirmwareErrorOf(act.error), "bad");
      return;
    }
    toast && toast("Campaign cancelled", "warn");
    setOpen(null);
    campsLive.reload && campsLive.reload();
  };

  const liveWrite = async (kind, id) => {
    if (!writeOk) {
      toast && toast("Your seat cannot change campaigns.", "warn");
      return;
    }
    const op = kind === "accept"
      ? "post__ota_campaigns_p_accept"
      : kind === "decline"
        ? "post__ota_campaigns_p_decline"
        : "post__ota_campaigns_p_rollback";
    const out = await act.run(path(op, { pathParams: [id] }), { method: "POST", body: {} });
    if (!out) {
      toast && toast(softFleetFirmwareErrorOf(act.error), "bad");
      return;
    }
    const lab = kind === "accept" ? "accepted" : kind === "decline" ? "declined" : "rolled back";
    if (kind === "accept") {
      const ack = out && out.device_ack_observed === true;
      const verified = out && (out.verified === true || out.accept_claims_verified === true);
      if (out && out.fanout === false) {
        toast && toast("Consent recorded · Base dispatch needs an OTA admin seat", "go");
      } else if (ack || verified) {
        toast && toast("Campaign accepted · device confirmed", "go");
      } else {
        toast && toast("Commands sent · waiting for device confirm", "warn");
      }
    } else {
      toast && toast("Campaign " + lab, "go");
    }
    setOpen(null);
    campsLive.reload && campsLive.reload();
  };

  const publishStored = async () => {
    if (!canPublish) {
      toast && toast("Your seat cannot publish board builds.", "warn");
      return;
    }
    const filename = String(pubFile || "").trim();
    if (!filename) {
      toast && toast("Pick a staged firmware file", "warn");
      return;
    }
    const body = { filename, target_admins: ["*"] };
    if (pubNotes.trim()) body.notes = pubNotes.trim();
    if (pubFw.trim()) body.fw_version = pubFw.trim();
    const out = await act.run(path("post__ota_campaigns_from-stored"), { method: "POST", body });
    if (!out) {
      toast && toast(softFleetFirmwareErrorOf(act.error), "bad");
      return;
    }
    toast && toast("Campaign published", "go");
    setPubNotes("");
    setPubFw("");
    campsLive.reload && campsLive.reload();
  };

  if (!posture.loading && !otaOk) {
    return html`<div className="fleet-fw-desk fleet-fw-desk--liquid"
      data-cmc-page="page138-firmware" data-cmc-legacy="page73-firmware"
      data-cmc-residual="page171-firmware">
      <div className="fleet-fw-desk__rail">
        <div>
          <p className="eyebrow">Fleet · Board updates</p>
          <h2 className="fleet-fw-desk__title">Board updates · seat closed</h2>
          <p className="note">${postureLabel}. No campaigns are offered on this grant.</p>
        </div>
        <div className="btn-row fleet-fw-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm" disabled=${posture.loading}
            onClick=${reRead}><${Ico} d="refresh" w=${14}/> Re-read</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/fleet/health")}>Fleet health</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/ota")}>Open OTA</button>
        </div>
      </div>
      <${Card} title="Board posture" meta="status">
        <p className="note">${postureLabel}</p>
        <p className="note">Role · ${post.principal_role || (me && me.role) || "—"}</p>
      <//>
      <${Alert} tone="info" title="Firmware administration not on this account"
        say=${"Campaign reads and rollout writes need a manager or OTA admin seat. This account is "
          + (post.principal_role || (me && me.role) || "operator")
          + " — the gap is not filled with pretend campaigns."}/>
      <${Card} title="Campaigns" meta="sealed">
        <p className="note">No campaigns are offered on this grant.</p>
      <//>
      <${Card} title="Builds on disk" meta="sealed">
        <p className="note">Firmware artifacts stay off this panel until the grant carries OTA publish.</p>
      <//>
    </div>`;
  }

  const ackMin = policy && Number(policy.device_ack_timeout_seconds) > 0
    ? Math.round(Number(policy.device_ack_timeout_seconds) / 60)
    : null;

  return html`<div className="fleet-fw-desk fleet-fw-desk--liquid desk-craft"
    data-cmc-page="page138-firmware" data-cmc-legacy="page73-firmware"
    data-cmc-residual="page171-firmware" data-ota-rail="board">
    <div className="fleet-fw-desk__rail">
      <div>
        <p className="eyebrow">Fleet · Board updates</p>
        <h2 className="fleet-fw-desk__title">Board campaigns</h2>
        <p className="note fleet-fw-desk__lede">
          Full-board image campaigns only. Publish needs a superadmin or OTA admin seat.
          Capability packs ship from Skills warehouse — not this desk.
        </p>
      </div>
      <div className="btn-row fleet-fw-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act" onClick=${reRead}
          disabled=${posture.loading || campsLive.loading || buildsLive.loading || policyLive.loading}>
          <${Ico} d="refresh" w=${14}/> Re-read
        </button>
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
          onClick=${() => go("/edge/skills")}>Skills warehouse</button>
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
          onClick=${() => go("/fleet/diagnose")}>Why offline</button>
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
          onClick=${() => go("/fleet/health")}>Fleet health</button>
        <button type="button" className="btn btn--ghost btn--sm desk-craft__act"
          onClick=${() => go("/ota")}>Open OTA</button>
      </div>
    </div>

    <${Alert} tone="warn" title="Board updates · not skills"
      say="This desk ships full firmware images to boards. Capability packs ship from OTA → Capabilities or Skills warehouse."/>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <${Alert} tone="info" title="Board campaign desk"
      say="Accept, decline, roll back, cancel, and publish are real writes when your seat allows them. Local pretend toggles stay off."/>

    <${Card} title="Firmware posture" meta="status">
      <div className="btn-row" style=${{ flexWrap: "wrap", gap: "8px 14px" }}>
        <${KV} k="posture" v=${postureLabel}/>
        <${KV} k="campaigns" v=${otaOk ? "open" : "closed"}/>
        <${KV} k="publish" v=${canPublish ? "open" : "closed"}/>
        <${KV} k="public base" v=${post.public_base_configured ? "set" : "missing"}/>
        <${KV} k="token" v=${post.token_configured ? "set" : "missing"}/>
      </div>
      ${detail && detail !== "ota_seat_gated"
        ? html`<p className="note" style=${{ marginTop: 8 }}>${String(detail)}</p>`
        : null}
    <//>

    ${liveErr ? html`
      <${Alert} tone="warn" title="Could not refresh every OTA read"
        say=${softFleetFirmwareErrorOf(liveErr)}/>` : null}

    <div className="fleet-fw-desk__kpis" role="group" aria-label="Firmware KPIs">
      ${[
        ["Campaigns", kpis.campaigns, kpis.campaigns ? "1" : "0", ""],
        ["Active", kpis.active, kpis.active ? "1" : "0", kpis.active ? "warn" : ""],
        ["Builds", kpis.builds, kpis.builds ? "1" : "0", ""],
        ["Policy", kpis.policy, kpis.policy ? "1" : "0", ""],
      ].map(([k, v, on, tone]) => html`
        <div key=${k} className="fleet-fw-desk__kpi" data-on=${on} data-tone=${tone}>
          <span>${k}</span>
          <b><${Ticker} value=${v}/></b>
          <em>board updates</em>
        </div>`)}
    </div>

    <${Card} title="Campaigns"
        meta=${campsLive.loading && !camps.length ? "reading…" : (camps.length ? (camps.length + " loaded") : "empty")}>
      <div className="chips" style=${{ marginBottom: 13 }}>
        <button type="button" className="chip" data-sel=${filter === "all" ? "1" : "0"}
          onClick=${() => setFilter("all")}>
          All <${Ticker} value=${camps.length}/></button>
        ${L.CAMPAIGN_STATES.map((s) => html`
          <button key=${s.id} type="button" className="chip" data-tone=${s.chip}
            data-sel=${filter === s.id ? "1" : "0"}
            onClick=${() => setFilter(s.id)}>
            ${s.t} <${Ticker} value=${count(s.id)}/></button>`)}
      </div>
      ${list.map((x) => html`
        <button key=${x.id} type="button" className="camp" data-state=${x.state}
          onClick=${() => setOpen(x.id)}>
          <span className="camp__st" data-state=${x.state}/>
          <span className="camp__t">
            <b>${x.fw}<code>${x.id}</code></b>
            <em>${x.notes || "—"}</em>
          </span>
          <span className="camp__runs">
            ${L.RUN_STATES.filter((s) => x.runs[s]).map((s) => html`
              <i key=${s} data-s=${s}>${x.runs[s]}<u>${s.replace("_", " ")}</u></i>`)}
          </span>
          <span className="camp__meta">${x.at}</span>
        </button>`)}
      ${!list.length && html`<p className="note">No campaigns loaded.</p>`}
    <//>

    <${Card} title="Builds on disk" meta="catalog">
      <table className="tbl">
        <thead><tr><th>File</th><th>Version</th><th>Hardware</th><th>SHA-256</th><th>Size</th><th>Notes</th></tr></thead>
        <tbody>
          ${!builds.length ? html`
            <tr><td colSpan="6"><p className="note" style=${{ margin: 0 }}>No firmware artifacts loaded.</p></td></tr>` : null}
          ${builds.map((f) => html`
            <tr key=${f.name}>
              <td><code>${f.name}</code></td>
              <td><b>${f.fw}</b></td>
              <td>${(f.sku || []).map((s) => html`<span key=${s} className="chip" data-tone="off">${s}</span>`)}</td>
              <td><code>${f.sha}</code></td>
              <td>${f.size}</td>
              <td>${f.notes ? html`<span className="chip">present</span>`
                : html`<span style=${{ color: "var(--fg-4)" }}>none</span>`}</td>
            </tr>`)}
        </tbody>
      </table>
      <p className="hint">Catalog depth and retention are server-side. This desk does not invent builds.</p>
    <//>

    <div className="grid g2">
      <${Card} title="OTA policy" meta=${policy ? "deployment" : "reading…"}
          sub="Deployment environment — console toggles that pretend to change these stay off.">
        ${!policy && !policyLive.error ? html`<p className="note">Reading policy…</p>` : null}
        ${policy ? html`<${F}>
          <${KV} k="Auto-rollback on failure" v=${policy.auto_rollback_on_failure ? "on" : "off"}/>
          <${KV} k="Device ack timeout" v=${policy.device_ack_timeout_seconds != null
            ? (policy.device_ack_timeout_seconds + "s"
              + (ackMin != null ? (" · ~" + ackMin + " min") : ""))
            : "—"}/>
          <${KV} k="Hardware match" v=${policy.hw_compat_enabled ? "on" : "off"}/>
          <${KV} k="Campaign reads" v=${policy.campaign_reads ? "armed" : "off"}/>
          <${KV} k="Campaign writes" v=${policy.campaign_writes ? "armed" : "off"}/>
          <${KV} k="Public OTA base" v=${policy.public_base_configured ? "configured" : "missing"}/>
          <${KV} k="OTA token" v=${policy.token_configured ? "configured" : "missing"}/>
          <p className="note">${policy.note || ""}</p>
        <//>` : null}
      <//>

      <${Card} title="Publish from staged build" meta="publish"
          sub=${canPublish
            ? "Creates a campaign from a file already staged on the OTA server."
            : "Publish needs a superadmin or OTA admin seat."}>
        ${!canPublish ? html`<p className="note">This grant can view campaigns but cannot publish.</p>` : html`<${F}>
          <${Field} label="Staged file">
            <select className="inp" value=${pubFile}
              onChange=${(e) => {
                const v = e.target.value;
                setPubFile(v);
                const hit = builds.find((b) => b.name === v);
                if (hit && hit.fw) setPubFw(String(hit.fw));
              }}>
              <option value="">Select a .bin…</option>
              ${builds.map((f) => html`<option key=${f.name} value=${f.name}>${f.name}${f.fw ? (" · " + f.fw) : ""}</option>`)}
            </select>
          <//>
          <${Field} label="Firmware version (optional override)">
            <${Input} value=${pubFw} onChange=${(e) => setPubFw(e.target.value)} placeholder="from .version / filename"/>
          <//>
          <${Field} label="Notes (optional)">
            <${Input} value=${pubNotes} onChange=${(e) => setPubNotes(e.target.value)} placeholder="rollout note"/>
          <//>
          <button type="button" className="btn btn--go btn--sm" disabled=${act.busy || !pubFile.trim()}
            onClick=${publishStored}>Publish campaign</button>
          ${!builds.length ? html`<p className="note">No staged builds — upload via board staging first.</p>` : null}
        <//>`}
      <//>
    </div>

    <${AnimatePresence}>
      ${c && html`
        <${M.div} key="c" className="scrim cmc-drawer-scrim" initial=${{ opacity: 0 }} animate=${{ opacity: 1 }}
            exit=${{ opacity: 0 }} transition=${FADE} onClick=${() => setOpen(null)}>
          <${M.div} className="sheetlet cmc-drawer" data-craft="v470" initial=${{ opacity: 0, y: 18 }} animate=${{ opacity: 1, y: 0 }}
              exit=${{ opacity: 0, y: 10 }} transition=${SOFT} onClick=${stop} role="dialog" aria-modal="true"
              aria-label=${"Campaign " + c.id}>
            <div className="cmc-drawer__in">
              <header className="cmc-drawer__hd">
                <div className="cmc-drawer__hd-copy">
                  <span className="cmc-drawer__k">Board campaign</span>
                  <h3 className="cmc-drawer__title">${c.fw}</h3>
                  <code className="cmc-drawer__id">${c.id}</code>
                </div>
                <button type="button" className="cmc-drawer__x" onClick=${() => setOpen(null)} aria-label="Close">
                  <${Ico} d="close" w=${13}/>
                </button>
              </header>
              ${c.notes ? html`<p className="cmc-drawer__lede">${c.notes}</p>` : null}
              <div className="cmc-drawer__state" data-state=${c.state}>
                <span>State</span><b>${c.state}</b>
                <em>${c.at || "—"}</em>
              </div>

              <section className="cmc-drawer__sec">
                <header className="cmc-drawer__sec-hd"><span>Offered to</span><b>${(c.admins || []).length}</b></header>
                <div className="cmc-drawer__list">
                  ${(c.admins || []).map((a) => html`
                    <div key=${a.u} className="cmc-drawer__row" data-d=${a.d}>
                      <b>${a.u}</b>
                      <span>${a.d === "—" ? "no answer" : a.d}</span>
                      <em>${a.at || "open"}</em>
                    </div>`)}
                </div>
                <p className="hint">A decline is not final — the same admin can accept later.</p>
              </section>

              <section className="cmc-drawer__sec">
                <header className="cmc-drawer__sec-hd"><span>Device runs</span></header>
                <div className="cmc-drawer__meters">
                  ${L.RUN_STATES.map((s) => html`
                    <div key=${s} className="cmc-drawer__m" data-s=${s}>
                      <b>${c.runs[s] || 0}</b><span>${s.replace("_", " ")}</span>
                    </div>`)}
                </div>
              </section>

              <section className="cmc-drawer__sec">
                <header className="cmc-drawer__sec-hd"><span>Confirm / stall</span><b>Update policy</b></header>
                <p className="note">Confirm timing comes from the deployment policy clock${policy && policy.device_ack_timeout_seconds != null
                    ? (": " + policy.device_ack_timeout_seconds + "s")
                    : " — wait for policy if blank."}</p>
              </section>

              <div className="cmc-drawer__acts btn-row">
                ${writeOk ? html`
                  <button type="button" className="btn btn--go btn--sm" disabled=${act.busy}
                    onClick=${() => liveWrite("accept", c.id)}>Accept</button>
                  <button type="button" className="btn btn--ghost btn--sm" disabled=${act.busy}
                    onClick=${() => liveWrite("decline", c.id)}>Decline</button>
                  <button type="button" className="btn btn--danger btn--sm" disabled=${act.busy}
                    onClick=${() => liveWrite("rollback", c.id)}>Roll back</button>
                  ${(c.state === "running" || c.state === "dispatched" || c.state === "partial") && html`
                    <button type="button" className="btn btn--danger btn--sm" disabled=${act.busy}
                      onClick=${() => cancel(c.id)}>
                      Cancel campaign</button>`}
                ` : html`<p className="note">This seat can view campaigns but cannot change them.</p>`}
              </div>
            </div>
          <//>
        <//>`}
    <//>
  </div>`;
}
